import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import { useConfig } from '@/composables/config'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { generateNonce } from '@/utils/crypto'

export const useMicrosoftAuthStore = defineStore('microsoftAuth', () => {
    const userStore = useUserStore()
    const loggerStore = useLoggerStore()

    const { VUE_APP_MSAL_CLIENT_ID, VUE_APP_MSAL_REDIRECT } = useConfig()

    const initialized = ref(false)
    const isSigningIn = ref(false)
    const msalInstance = shallowRef<InstanceType<typeof import('@azure/msal-browser').PublicClientApplication>>()
    const nonce = ref<{ rawNonce: string; hashedNonce: string }>()

    async function initGlobalSignIn() {
        if (!initialized.value && !msalInstance.value) {
            nonce.value = await generateNonce()

            const { PublicClientApplication } = await import('@azure/msal-browser')
            msalInstance.value = new PublicClientApplication({
                auth: {
                    clientId: VUE_APP_MSAL_CLIENT_ID,
                    redirectUri: `${window.location.origin}/ms-auth-callback.html`,
                },
            })
            await msalInstance.value.initialize()
            initialized.value = true
        }
    }

    async function signIn(onFinish: (isSuccess: boolean, errorMessage?: string) => void) {
        if (!msalInstance.value || !initialized.value || isSigningIn.value) {
            return
        }

        isSigningIn.value = true
        let errorMessage: string | undefined

        try {
            const { idToken } = await msalInstance.value.loginPopup({
                scopes: ['openid', 'profile', 'email'],
                prompt: 'select_account',
                nonce: nonce.value?.hashedNonce,
            })

            const res = await fetch(VUE_APP_MSAL_REDIRECT, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    credential: idToken,
                    nonce: nonce.value?.rawNonce,
                }),
            })

            if (res.status > 399) {
                const parsed = await res.json()
                errorMessage = parsed.message
            }
        } catch (e) {
            errorMessage = (e as Error).message

            if (errorMessage === 'user_cancelled: User cancelled the flow.') {
                // we don't treat user cancellation as an error
                return
            }
        } finally {
            isSigningIn.value = false
        }

        await userStore.fetchUserInfo()

        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'login',
            action: userStore.isAuthorized ? 'success' : 'fail',
        })

        onFinish(userStore.isAuthorized, errorMessage)
    }

    return {
        initGlobalSignIn,
        signIn,
        initialized,
        isSigningIn,
    }
})
